import React from 'react';
import { API_URL } from '../services/api';

interface FileUploadProps {
  label: string;
  accept: string;
  file: File | null;
  existingFile?: string; // For editing mode - shows current file
  onFileChange: (file: File | null) => void;
  onClear?: () => void;
  placeholder?: string;
  disabled?: boolean;
  multiple?: boolean;
  showPreview?: boolean;
  maxPreviewHeight?: number;
  className?: string;
}

const FileUpload: React.FC<FileUploadProps> = ({
  label,
  accept,
  file,
  existingFile,
  onFileChange,
  onClear,
  placeholder = "Choose file...",
  disabled = false,
  multiple = false,
  showPreview = true,
  maxPreviewHeight = 150,
  className = "form-control"
}) => {
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0] || null;
    onFileChange(selectedFile);
  };

  const handleClear = () => {
    onFileChange(null);
    if (onClear) onClear();
    // Reset the input value
    const input = document.querySelector(`input[type="file"][accept="${accept}"]`) as HTMLInputElement;
    if (input) input.value = '';
  };

  const getPreviewUrl = () => {
    if (file) {
      return URL.createObjectURL(file);
    } else if (existingFile) {
      return `${API_URL}/uploads/${existingFile}`;
    }
    return null;
  };

  const getFileName = () => {
    if (file) {
      return file.name;
    } else if (existingFile) {
      return existingFile;
    }
    return null;
  };

  const isImage = accept.includes('image');
  const isAudio = accept.includes('audio');
  const previewUrl = getPreviewUrl();
  const fileName = getFileName();

  return (
    <div style={{ marginBottom: '16px' }}>
      <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>{label}</label>
      
      <div style={{ position: 'relative' }}>
        <input
          type="file"
          accept={accept}
          onChange={handleFileChange}
          className={className}
          disabled={disabled}
          multiple={multiple}
          style={{ display: file || existingFile ? 'none' : 'block' }}
        />
        
        {(file || existingFile) && (
          <div style={{
            padding: '12px',
            border: '1px solid rgba(255,255,255,0.2)',
            borderRadius: '8px',
            background: 'rgba(255,255,255,0.05)'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '8px'
            }}>
              <span style={{
                fontSize: '14px',
                color: 'rgba(255,255,255,0.9)',
                flex: 1,
                marginRight: '8px',
                wordBreak: 'break-all'
              }}>
                {fileName}
              </span>
              <button
                type="button"
                className="btn btn-small btn-outline"
                onClick={handleClear}
                disabled={disabled}
                title="Remove file"
                style={{
                  background: 'rgba(244, 67, 54, 0.1)',
                  border: '1px solid rgba(244, 67, 54, 0.3)',
                  color: '#f44336',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '12px'
                }}
              >
                ✕
              </button>
            </div>
            
            {showPreview && previewUrl && (
              <div style={{ marginTop: '8px', textAlign: 'center' }}>
                {isImage && (
                  <img
                    src={previewUrl}
                    alt="Preview"
                    style={{
                      maxHeight: maxPreviewHeight,
                      maxWidth: '100%',
                      borderRadius: '6px',
                      border: '1px solid rgba(255,255,255,0.2)'
                    }}
                  />
                )}
                
                {isAudio && (
                  <audio
                    controls
                    style={{ width: '100%', maxWidth: '300px' }}
                  >
                    <source src={previewUrl} type="audio/mpeg" />
                    Your browser does not support the audio element.
                  </audio>
                )}
              </div>
            )}
            
            <button
              type="button"
              className="btn btn-outline btn-small"
              onClick={() => {
                const input = document.querySelector(`input[type="file"][accept="${accept}"]`) as HTMLInputElement;
                if (input) input.click();
              }}
              disabled={disabled}
              style={{ marginTop: '8px' }}
            >
              Change File
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default FileUpload;
